import debounce from 'lodash/debounce';
import { CasClient } from '@central-products/cas-client';
import {
  SessionMarkActiveConfig,
  SessionMarkActiveServiceInterface,
  SessionMarkActiveServiceSessionExpiration,
  SessionMarkActiveServiceIamAliveStateInterface,
} from '../types';
import { SESSION_EXPIRATION_INITIAL_STATE } from '../constants';

const FIFTEEN_MINUTES = 900000;
const HALF_MINUTE = 30000;
const TEN_SECONDS = 10000;
const TWO_SECONDS = 2000;

export class SessionMarkActiveService implements SessionMarkActiveServiceInterface {
  private readonly casClient: CasClient;
  private readonly onErrorCallback: () => void;
  private readonly onImAliveCallback: (response: SessionMarkActiveServiceSessionExpiration) => void;
  private readonly iamAliveDebounceMaxWaitTime: number;
  private readonly iamAliveDebounceTime: number;
  private readonly retryMaxTime: number;
  private readonly retryInterval: number;
  private readonly debouncedIamAlive: (() => void) & { cancel: () => void; flush: () => void };
  private readonly debouncedIamAliveStateChange: () => void;
  private readonly debouncedIamAliveBound: (() => void) & { cancel?: () => void };
  private retryMaxTimeout?: number | null;
  private retryTimeout?: number | null;

  constructor({
    casClient,
    onErrorCallback,
    onImAliveCallback,
    iamAliveDebounceMaxWaitTime = FIFTEEN_MINUTES,
    iamAliveDebounceTime = HALF_MINUTE,
    retryInterval = TWO_SECONDS,
    retryMaxTime = TEN_SECONDS,
  }: SessionMarkActiveConfig & {
    casClient: CasClient;
    onErrorCallback: () => void;
    onImAliveCallback: (response: SessionMarkActiveServiceSessionExpiration) => void;
  }) {
    if (iamAliveDebounceMaxWaitTime < iamAliveDebounceTime) {
      throw Error('iamAliveDebounceMaxWaitTime should not be smaller than iamAliveDebounceTime');
    }
    this.casClient = casClient;
    this.onErrorCallback = onErrorCallback;
    this.onImAliveCallback = onImAliveCallback;
    this.iamAliveDebounceMaxWaitTime = iamAliveDebounceMaxWaitTime;
    this.iamAliveDebounceTime = iamAliveDebounceTime;
    this.retryInterval = retryInterval;
    this.retryMaxTime = retryMaxTime;
    this.debouncedIamAlive = debounce(this.imAlive, this.iamAliveDebounceTime, {
      maxWait: this.iamAliveDebounceMaxWaitTime,
    });
    this.debouncedIamAliveStateChange = debounce(this.imAliveChangeState, this.iamAliveDebounceTime, {
      maxWait: this.iamAliveDebounceMaxWaitTime,
    });
    this.debouncedIamAliveBound = this.debouncedIamAliveStateChange.bind(this);
  }

  public imAlive(): Promise<SessionMarkActiveServiceIamAliveStateInterface> {
    if (this.retryMaxTimeout) {
      throw Error('there is an ongoing retry iAmAlive call');
    }

    this.setRetryMaxTimer();
    return this.callIamAlive();
  }

  public markActive(): void {
    this.debouncedIamAlive();
  }

  private async imAliveChangeState() {
    const { timeToKill, expiresAt } = await this.imAlive();
    this.onImAliveCallback({ timeToKill, expiresAt });
  }

  public trackUserActivity(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('mousedown', this.debouncedIamAliveBound);
      window.addEventListener('mouseup', this.debouncedIamAliveBound);
      window.addEventListener('touchstart', this.debouncedIamAliveBound);
      window.addEventListener('touchmove', this.debouncedIamAliveBound);
      window.addEventListener('keydown', this.debouncedIamAliveBound);
      window.addEventListener('keyup', this.debouncedIamAliveBound);
    }
  }

  public untrackUserActivity(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('mousedown', this.debouncedIamAliveBound);
      window.removeEventListener('mouseup', this.debouncedIamAliveBound);
      window.removeEventListener('touchstart', this.debouncedIamAliveBound);
      window.removeEventListener('touchmove', this.debouncedIamAliveBound);
      window.removeEventListener('keydown', this.debouncedIamAliveBound);
      window.removeEventListener('keyup', this.debouncedIamAliveBound);
    }
  }

  public dispose(): void {
    this.debouncedIamAlive.cancel();
    if (typeof this.debouncedIamAliveBound.cancel === 'function') {
      this.debouncedIamAliveBound.cancel();
    }
  }

  private shouldRetry({ status }: { status?: number }): boolean {
    // In case of connection error we just retry until connection is back
    if (!status) {
      return true;
    }

    if (status === 401) {
      return false;
    }

    return !!this.retryMaxTimeout;
  }

  private callIamAlive = () =>
    new Promise<SessionMarkActiveServiceIamAliveStateInterface>((resolve) => {
      this.casClient
        .imAlive()
        .then(({ imAlive, expiresAt, timeToKill }) => {
          this.clearRetryTimer();
          resolve({ expiresAt, timeToKill, imAlive });
        })
        .catch((error: { status?: number }) => {
          const shouldRetry = this.shouldRetry(error);
          if (shouldRetry) {
            this.retry().then(resolve);
          } else {
            this.clearRetryTimer();
            this.onErrorCallback();
            resolve({ imAlive: false, ...SESSION_EXPIRATION_INITIAL_STATE });
          }
        });
    });

  private clearRetryTimer = () => {
    this.clearRetryMaxTimer();
    if (this.retryTimeout) {
      window.clearInterval(this.retryTimeout);
      this.retryTimeout = null;
    }
  };

  private retry = () =>
    new Promise<SessionMarkActiveServiceIamAliveStateInterface>((resolve) => {
      this.retryTimeout = window.setTimeout(() => {
        this.retryTimeout = null;
        this.callIamAlive().then(resolve);
      }, this.retryInterval);
    });

  private clearRetryMaxTimer = () => {
    if (this.retryMaxTimeout) {
      window.clearTimeout(this.retryMaxTimeout);
      this.retryMaxTimeout = null;
    }
  };

  private setRetryMaxTimer = () => {
    this.retryMaxTimeout = window.setTimeout(() => this.clearRetryMaxTimer(), this.retryMaxTime);
  };
}
